"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.recordIpAdvanceSchema = exports.ipAdvanceTenderSchema = exports.bindDepositSchema = exports.reverseSettlementSchema = exports.settleAdmissionSchema = exports.ipSettlementStatusEnum = void 0;
const zod_1 = require("zod");
/** IP final settlement contracts — ADR-085 Phase 9. */
exports.ipSettlementStatusEnum = zod_1.z.enum(['DRAFT', 'SETTLED', 'REVERSED']);
/**
 * Settlement takes NO financial totals. Every figure is computed server-side
 * from the immutable ledgers — a caller-supplied total would be a number the
 * settlement could not later justify, and this document is the legal one.
 *
 * `roundOffAmount` is the single exception, and it applies to the PAYABLE TOTAL
 * only. Rounding a line, or rounding before tax, changes the tax base and
 * produces a GST return that does not tie to the invoice (ADR-062 §3).
 */
exports.settleAdmissionSchema = zod_1.z.object({
    roundOffAmount: zod_1.z.number().int().min(-99).max(99).optional(),
    remarks: zod_1.z.string().max(1000).optional(),
});
exports.reverseSettlementSchema = zod_1.z.object({
    reason: zod_1.z.string().min(1).max(1000),
});
exports.bindDepositSchema = zod_1.z.object({
    depositAccountId: zod_1.z.string().uuid(),
    depositTransactionId: zod_1.z.string().uuid().optional(),
    demandedAmount: zod_1.z.number().int().nonnegative().default(0),
    collectedAmount: zod_1.z.number().int().nonnegative(),
    notes: zod_1.z.string().max(500).optional(),
});
/**
 * TAKING AN ADVANCE at the ward desk.
 *
 * CONTEXT, NOT BOOKKEEPING. The desk states how much was collected and how it
 * came in; the server finds or opens the patient's advance account, writes the
 * append-only ledger movement and binds the result to the stay.
 *
 * IT NOW ISSUES A RECEIPT, AND THAT CHANGED WHAT THIS ENDPOINT IS.
 *
 * It used to create no Payment row at all — the money moved in the deposit
 * ledger and the desk had nothing to hand over. An advance is a counter
 * collection like any other, so it now goes through the same path an OP bill
 * payment does: a `FinPayment` attributed to the cashier's counter and session
 * (which is what puts the cash in the drawer at settlement), and a `FinReceipt`
 * numbered in the OP BILL series — one continuous document run across the
 * counter, by explicit client decision. The GL posting stays the deposit
 * engine's `DEPOSIT_RECEIVED`; the payment is NOT posted a second time.
 */
/**
 * ONE PAYMENT LINE of a collection taken at the counter.
 *
 * The counter has always been able to take ₹5,000 as ₹2,000 cash and ₹3,000 by
 * UPI — `CollectionContextResolver` and `PaymentProcessingService` have accepted
 * a list of tenders since the OP bill learned to split. This endpoint could not
 * express it: it asked "how was it paid?" once, so the desk picked whichever
 * mode was larger and the rest was recorded as something it was not.
 *
 * THE BANK ACCOUNT AND THE REFERENCE BELONG TO THE LINE, not to the collection.
 * A split settles in more than one place — the cash into the drawer, the UPI
 * into HDFC — and one account named for the whole payment could only ever
 * describe one of them. The UTR is the same: a split has two.
 */
exports.ipAdvanceTenderSchema = zod_1.z.object({
    paymentModeId: zod_1.z.string().uuid(),
    /** Integer paise, positive — a line for zero is not a payment. */
    amountPaise: zod_1.z.number().int().positive(),
    bankAccountId: zod_1.z.string().uuid().optional(),
    referenceNumber: zod_1.z.string().max(100).optional(),
});
exports.recordIpAdvanceSchema = zod_1.z.object({
    /**
     * Integer paise, and positive — an advance of zero is not a transaction.
     *
     * Still the authority on WHAT WAS COLLECTED even when `tenders` is present:
     * the lines must add up to exactly this, and the refinement below refuses the
     * request when they do not. Deriving the total from the lines instead would
     * mean a client that mistyped one line silently gets a receipt for a
     * different amount than the one it displayed.
     */
    amount: zod_1.z.number().int().positive(),
    /** What the desk ASKED for, when a deposit rule demanded a figure. */
    demandedAmount: zod_1.z.number().int().nonnegative().default(0),
    notes: zod_1.z.string().max(500).optional(),
    /**
     * HOW the advance was paid, so the ledger can say where the money went.
     *
     * Optional, because every advance recorded before this existed had no tender
     * at all and must keep working. When present, a non-cash mode MUST name the
     * bank account it settles into — `SettlementAccountResolver` enforces that
     * and resolves the debit leg from it. Without this the advance debited
     * `1110 Cash` for every mode, which is the same defect that put ₹12,043 of
     * card and UPI money into cash-in-hand on the billing side.
     */
    paymentModeId: zod_1.z.string().uuid().optional(),
    bankAccountId: zod_1.z.string().uuid().optional(),
    referenceNumber: zod_1.z.string().max(100).optional(),
    /**
     * THE SPLIT — every line of the collection, when there is more than one.
     *
     * Mutually exclusive with the three single-tender fields above rather than
     * merged with them: a request naming both states the tender twice, and there
     * is no reading of "cash, and also these two lines" that a ledger can post
     * without guessing. Ten is the same ceiling the OP counter carries.
     */
    tenders: zod_1.z.array(exports.ipAdvanceTenderSchema).min(1).max(10).optional(),
    /**
     * WHERE the money was taken — the cash counter the operator is sitting at.
     *
     * Optional only so the pre-receipt callers keep working; without it there is
     * no session to attribute the collection to and the cash never appears in
     * that cashier's drawer at settlement. The counter screen always sends it.
     */
    counterId: zod_1.z.string().uuid().optional(),
    /**
     * Named by the client, VERIFIED by the server against the session it resolves
     * for (counter, cashier) — never trusted. Naming someone else's open session
     * is refused rather than silently re-attributed.
     */
    cashSessionId: zod_1.z.string().uuid().optional(),
    /**
     * MINTED ONCE PER ATTEMPT, same convention as `VendorDebitNoteWorkstationPage`
     * / `VendorCreditNoteWorkstationPage`'s own `idempotencyKey`. Optional so
     * every caller that predates this keeps working unconditionally; a client
     * that sends one is protected by `IdempotencyService` (`@@unique([tenantId,
     * endpoint, idempotencyKey])`) against recording the SAME advance twice — a
     * double-click on "Confirm" before the button disables, or a retried
     * request, mints one advance rather than two.
     */
    idempotencyKey: zod_1.z.string().uuid().optional(),
}).superRefine((input, ctx) => {
    if (!input.tenders)
        return;
    /**
     * ONE STATEMENT OF THE TENDER. Accepting both shapes at once would leave the
     * server choosing which one the operator meant, and whichever it chose would
     * be right half the time.
     */
    for (const field of ['paymentModeId', 'bankAccountId', 'referenceNumber']) {
        if (input[field] !== undefined) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: [field],
                message: `A split collection states every line in "tenders" — remove "${field}".`,
            });
        }
    }
    /**
     * THE LINES MUST ADD UP TO THE COLLECTION.
     *
     * Not a rounding nuisance: the deposit credited, the receipt printed and the
     * journals posted are all driven from these two figures, so a disagreement
     * here is a receipt that says one thing while the ledger says another. Refuse
     * rather than pick a side — the same rule `recordSettlementEventsForPayment`
     * enforces on the billing side.
     */
    const lines = input.tenders.reduce((sum, tender) => sum + tender.amountPaise, 0);
    if (lines !== input.amount) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['tenders'],
            message: `The payment lines total ${lines} paise but the collection is ${input.amount}.`,
        });
    }
});
//# sourceMappingURL=ip-settlement.schema.js.map